/**
 * Starter AppShell — the canonical sidebar + main composition for
 * future product screens.
 *
 * The `.main` column is a FIXED-HEIGHT card (`overflow: hidden`): the top-bar is a
 * docked, non-scrolling header at the top, and `<main className="content">` is the
 * only scroll region. The page (window) never scrolls. See AppShell.module.css.
 *
 * Layout:
 *   - Desktop (≥1280px): CSS grid with a fixed 240px sidebar column on
 *     the left, main column on the right. The top-bar docks at the top of the
 *     rounded card; the content region below it scrolls internally.
 *   - Mobile (<1280px): single column. The sidebar prop is hidden by
 *     default (consumers can render it inside a Drawer driven by an
 *     external state machine — AppShell itself stays presentational so
 *     it doesn't fight TanStack Router or external nav state).
 *
 * Pure CSS-Module primitive — does not depend on Mantine's AppShell.
 * The current `_protected.tsx` keeps its header-only layout; this shell
 * is for new screens (e.g. a full Dashboard).
 */
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import styles from './AppShell.module.css';

export interface AppShellProps extends HTMLAttributes<HTMLDivElement> {
  /** Sidebar content — typically `<Sidebar>...</Sidebar>`. */
  readonly sidebar?: ReactNode;
  /** Optional top-bar above the main column — typically `<TopBar />`. */
  readonly topBar?: ReactNode;
  /**
   * Optional app-level banner region rendered below the docked top-bar and
   * above `<main>` — an extension point for app-wide notices. Renders nothing
   * when the passed node is null, so it costs no layout when idle.
   */
  readonly banner?: ReactNode;
  /**
   * Collapse the desktop rail to an icons-only width, freeing the reserved grid
   * column so the content gets the extra room. Desktop-only (≥1280px); below
   * that the rail is hidden and consumers mount it in a Drawer.
   */
  readonly railCollapsed?: boolean;
  /**
   * Float the (visually expanded) rail OVER the content instead of reserving its
   * full width in the grid — the hover-expand affordance for a collapsed rail.
   * The grid column stays at the collapsed width (content does not reflow) and
   * the rail gets a subtle edge shadow. Only meaningful with `railCollapsed`.
   */
  readonly railOverlay?: boolean;
  /** Rail hover handlers — drive the collapsed-rail hover-to-expand overlay. */
  readonly onRailMouseEnter?: (() => void) | undefined;
  readonly onRailMouseLeave?: (() => void) | undefined;
  /**
   * Content width. `contained` (default) caps the page content at `--max-content`
   * and centers it — the right choice for reading-width surfaces (forms, settings,
   * the uploader). `full` removes the cap so the content uses the whole card width,
   * for data-dense surfaces that need the room (Modeling, Portfolio). The `.content`
   * padding is kept either way.
   */
  readonly contentWidth?: 'contained' | 'full';
  readonly children?: ReactNode;
}

export const AppShell = forwardRef<HTMLDivElement, AppShellProps>(
  function AppShell(
    {
      sidebar,
      topBar,
      banner,
      railCollapsed = false,
      railOverlay = false,
      onRailMouseEnter,
      onRailMouseLeave,
      contentWidth = 'contained',
      className,
      children,
      ...rest
    },
    ref,
  ) {
    const classes = [styles.shell, className].filter(Boolean).join(' ');
    return (
      <div
        ref={ref}
        className={classes}
        data-rail={railCollapsed ? 'collapsed' : 'expanded'}
        {...(railOverlay ? { 'data-rail-overlay': 'true' } : {})}
        {...rest}
      >
        {sidebar !== undefined ? (
          <aside
            className={styles.aside}
            onMouseEnter={onRailMouseEnter}
            onMouseLeave={onRailMouseLeave}
          >
            <div className={styles.rail}>{sidebar}</div>
          </aside>
        ) : null}
        <div className={styles.main}>
          {topBar !== undefined ? (
            <div className={styles.topBar}>{topBar}</div>
          ) : null}
          {banner !== undefined ? banner : null}
          {/* The card is fixed-height with `overflow: hidden`; `.content` is the
              ONLY scroll region (the page itself never scrolls), and `.contentInner`
              keeps the page content max-width-centered while the scrollbar sits at
              the card's edge. See AppShell.module.css. */}
          <main className={styles.content} data-scroll-restoration-id="app-content">
            <div
              className={[
                styles.contentInner,
                contentWidth === 'full' ? styles.contentInnerFull : null,
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {children}
            </div>
          </main>
        </div>
      </div>
    );
  },
);
